import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import axiosInstance from "@/shared/services/axiosInstance";
import {
  ApiEnvelope,
  BatchStatusData,
  BillingData,
  BillingQueryParams,
  DuplicatesData,
  DuplicatesQueryParams,
  ExportBatchResult,
  FileValidationData,
  MeterReadingsData,
  MeterReadingsQueryParams,
  MissingDataQueryParams,
  MissingDataResponse,
  Nem12ApiError,
  Nem12ApiResult,
  SummaryData,
  SummaryQueryParams,
  UploadBatchData,
} from "../types/nem12Viewer.types";
import {
  mapErrorEnvelope,
  parseContentDispositionFileName,
} from "../utils/nem12Viewer.helpers";

const getNem12ApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_NEM12_API_URL;
  if (!envUrl || typeof envUrl !== "string") {
    throw new Error("VITE_NEM12_API_URL is not configured.");
  }

  return envUrl.replace(/\/$/, "");
};

const buildUrl = (
  path: string,
  queryParams?: Record<string, string | number | boolean | undefined | null>,
): string => {
  const baseUrl = getNem12ApiBaseUrl();
  const url = new URL(`${baseUrl}${path}`);

  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        return;
      }
      url.searchParams.set(key, String(value));
    });
  }

  return url.toString();
};

const parseBlobToJson = async <TData>(blob: Blob): Promise<TData | null> => {
  try {
    const text = await blob.text();
    if (!text) {
      return null;
    }
    return JSON.parse(text) as TData;
  } catch {
    return null;
  }
};

const normalizeRequestMethod = (
  method?: AxiosRequestConfig["method"],
): string => {
  if (!method) {
    return "GET";
  }

  return String(method).toUpperCase();
};

const withMethodHeaders = (
  requestConfig: AxiosRequestConfig,
): AxiosRequestConfig => {
  const requestMethod = normalizeRequestMethod(requestConfig.method);

  return {
    ...requestConfig,
    headers: {
      ...(requestConfig.headers || {}),
      "Request-Method": requestMethod,
    },
  };
};

const parseEnvelopeResponse = <TData>(
  response: AxiosResponse<ApiEnvelope<TData>>,
): Nem12ApiResult<TData> => {
  const payload = response.data as
    | ApiEnvelope<TData>
    | {
        error?: {
          code?: string;
          message?: string;
          details?: Record<string, unknown>;
        };
      };

  if (
    payload &&
    typeof payload === "object" &&
    "error" in payload &&
    payload.error
  ) {
    return {
      statusCode: response.status,
      isSuccess: false,
      isProcessing: false,
      error: {
        statusCode: response.status,
        code: payload.error.code || "UNKNOWN_ERROR",
        message: payload.error.message || "Request failed.",
        details: payload.error.details,
      },
    };
  }

  const envelope = response.data as ApiEnvelope<TData>;
  const status = envelope.batch?.status;

  return {
    statusCode: response.status,
    isSuccess: response.status >= 200 && response.status < 300,
    isProcessing:
      response.status === 202 || status === "Queued" || status === "Processing",
    data: envelope,
  };
};

const envelopeRequest = async <TData>(
  requestConfig: AxiosRequestConfig,
): Promise<Nem12ApiResult<TData>> => {
  try {
    const requestWithHeaders = withMethodHeaders(requestConfig);
    const response = await axiosInstance.request<ApiEnvelope<TData>>({
      validateStatus: () => true,
      ...requestWithHeaders,
    });

    return parseEnvelopeResponse<TData>(response);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const responseStatus = error.response?.status;
      const responseData = error.response?.data;
      if (
        responseData &&
        typeof responseData === "object" &&
        "error" in (responseData as Record<string, unknown>)
      ) {
        return {
          statusCode: responseStatus ?? 500,
          isSuccess: false,
          isProcessing: false,
          error: mapErrorEnvelope(responseData as never, responseStatus),
        };
      }

      return {
        statusCode: responseStatus ?? 500,
        isSuccess: false,
        isProcessing: false,
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Request failed.",
          statusCode: responseStatus,
        },
      };
    }

    return {
      statusCode: 500,
      isSuccess: false,
      isProcessing: false,
      error: {
        code: "UNKNOWN_ERROR",
        message: "Unexpected error occurred.",
      },
    };
  }
};

export const uploadNem12Batch = async (
  files: File[],
): Promise<Nem12ApiResult<UploadBatchData>> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });

  return envelopeRequest<UploadBatchData>({
    method: "post",
    url: buildUrl("/v1/nem12-batches"),
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getNem12BatchStatus = async (
  batchId: string,
): Promise<Nem12ApiResult<BatchStatusData>> => {
  return envelopeRequest<BatchStatusData>({
    method: "get",
    url: buildUrl(`/v1/nem12-batches/${encodeURIComponent(batchId)}`),
    skipGlobalLoading: true,
  });
};

export const getNem12Summary = async (
  batchId: string,
  params: SummaryQueryParams,
): Promise<Nem12ApiResult<SummaryData>> => {
  return envelopeRequest<SummaryData>({
    method: "get",
    url: buildUrl(`/v1/nem12-batches/${encodeURIComponent(batchId)}/summary`, {
      nmi: params.nmi,
      serial: params.serial,
      sort: params.sort,
      order: params.order,
      pageSize: params.pageSize,
      cursor: params.cursor,
    }),
    skipGlobalLoading: true,
  });
};

export const getNem12Billing = async (
  batchId: string,
  params: BillingQueryParams,
): Promise<Nem12ApiResult<BillingData>> => {
  return envelopeRequest<BillingData>({
    method: "get",
    url: buildUrl(`/v1/nem12-batches/${encodeURIComponent(batchId)}/billing`, {
      nmi: params.nmi,
      serial: params.serial,
      sort: params.sort,
      order: params.order,
      pageSize: params.pageSize,
      cursor: params.cursor,
    }),
    skipGlobalLoading: true,
  });
};

export const getNem12MissingData = async (
  batchId: string,
  params: MissingDataQueryParams,
): Promise<Nem12ApiResult<MissingDataResponse>> => {
  return envelopeRequest<MissingDataResponse>({
    method: "get",
    url: buildUrl(
      `/v1/nem12-batches/${encodeURIComponent(batchId)}/missing-data`,
      {
        nmi: params.nmi,
        serial: params.serial,
        nmiSuffix: params.nmiSuffix,
        sort: params.sort,
        order: params.order,
        pageSize: params.pageSize,
        cursor: params.cursor,
      },
    ),
    skipGlobalLoading: true,
  });
};

export const getNem12FileValidation = async (
  batchId: string,
): Promise<Nem12ApiResult<FileValidationData>> => {
  return envelopeRequest<FileValidationData>({
    method: "get",
    url: buildUrl(
      `/v1/nem12-batches/${encodeURIComponent(batchId)}/validation`,
    ),
  });
};

export const getNem12MeterReadings = async (
  batchId: string,
  nmi: string,
  serial: string,
  params: MeterReadingsQueryParams,
): Promise<Nem12ApiResult<MeterReadingsData>> => {
  const includeValues = new Set<string>(["b2b"]);
  if (params.include) {
    params.include
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .forEach((item) => includeValues.add(item));
  }

  return envelopeRequest<MeterReadingsData>({
    method: "get",
    url: buildUrl(
      `/v1/nem12-batches/${encodeURIComponent(batchId)}/meters/${encodeURIComponent(nmi)}/${encodeURIComponent(serial)}/readings`,
      {
        cursor: params.cursor,
        pageSize: params.pageSize,
        include: Array.from(includeValues).join(","),
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        quality: params.quality,
        channel: params.channel,
      },
    ),
    skipGlobalLoading: true,
  });
};

const toNem12ApiError = (
  statusCode: number,
  fallbackMessage: string,
  maybeEnvelope?: {
    error?: {
      code?: string;
      message?: string;
      details?: Record<string, unknown>;
    };
  },
): Nem12ApiError => {
  if (maybeEnvelope?.error) {
    return {
      statusCode,
      code: maybeEnvelope.error.code || "UNKNOWN_ERROR",
      message: maybeEnvelope.error.message || fallbackMessage,
      details: maybeEnvelope.error.details,
    };
  }

  return {
    statusCode,
    code: "UNKNOWN_ERROR",
    message: fallbackMessage,
  };
};

export const exportNem12Batch = async (
  batchId: string,
  exportScope?: string,
): Promise<ExportBatchResult> => {
  try {
    const exportRequestConfig = withMethodHeaders({
      method: "get",
      url: buildUrl(`/v1/nem12-batches/${encodeURIComponent(batchId)}/export`, {
        scope: exportScope,
        type: exportScope,
        include: exportScope,
      }),
      responseType: "blob",
      validateStatus: () => true,
    });

    const response = await axiosInstance.request<Blob>({
      ...exportRequestConfig,
    });

    const contentType = response.headers["content-type"] as string | undefined;
    const contentDisposition = response.headers["content-disposition"] as
      | string
      | undefined;

    if (response.status === 202) {
      const jsonEnvelope = await parseBlobToJson<{
        error?: { message?: string };
      }>(response.data);
      return {
        kind: "processing",
        message:
          jsonEnvelope?.error?.message ||
          "Batch is still processing. Please try export again shortly.",
        statusCode: 202,
      };
    }

    if (response.status >= 400 || contentType?.includes("application/json")) {
      const jsonEnvelope = await parseBlobToJson<{
        error?: {
          code?: string;
          message?: string;
          details?: Record<string, unknown>;
        };
      }>(response.data);

      return {
        kind: "error",
        statusCode: response.status,
        error: toNem12ApiError(
          response.status,
          "Failed to export batch.",
          jsonEnvelope ?? undefined,
        ),
      };
    }

    const fallback = `${batchId}_export.zip`;
    return {
      kind: "zip",
      blob: response.data,
      fileName: parseContentDispositionFileName(contentDisposition, fallback),
    };
  } catch (error) {
    return {
      kind: "error",
      statusCode: 500,
      error: {
        code: "EXPORT_FAILED",
        message:
          error instanceof Error ? error.message : "Failed to export batch.",
      },
    };
  }
};

export const getDuplicatesRecords = async (
  batchId: string,
  params: DuplicatesQueryParams,
): Promise<Nem12ApiResult<DuplicatesData>> => {
  return envelopeRequest<DuplicatesData>({
    method: "get",
    url: buildUrl(
      `/v1/nem12-batches/${encodeURIComponent(batchId)}/duplicates`,
      {
        nmi: params.nmi,
        serial: params.serial,
        kind: params.kind,
        sort: params.sort,
        order: params.order,
        pageSize: params.pageSize,
        cursor: params.cursor,
        page: params.page,
      },
    ),
    skipGlobalLoading: true,
  });
};

export const clearBatch = async (
  batchId: string,
): Promise<Nem12ApiResult<BatchStatusData>> => {
  return envelopeRequest<BatchStatusData>({
    method: "delete",
    url: buildUrl(`/v1/nem12-batches/${encodeURIComponent(batchId)}/clear`),
  });
};
